import type { Dayjs } from "dayjs";
import { dayjs } from "./date";

/**
 * A group of timeline entries that share the same local day.
 */
export interface TimelineDayGroup<TItem> {
  /**
   * Local day key in `YYYY-MM-DD` format identifying the group
   * ("invalid" when the anchor timestamp could not be parsed).
   */
  dayKey: string;
  /**
   * pt-BR separator label for the group — "Hoje", "Ontem" or "DD/MM/YYYY"
   * ("Sem data" when the day key is not a valid date).
   */
  label: string;
  /** Entries that fall on that day, kept in input order. */
  entries: TItem[];
}

const INVALID_DAY_KEY = "invalid";

/**
 * Returns the local day key (`YYYY-MM-DD`) for a timestamp, or "invalid" when
 * the timestamp cannot be parsed.
 *
 * @param timestamp - ISO-8601 timestamp string.
 * @returns The local day key in `YYYY-MM-DD` format, or "invalid".
 */
export function timelineDayKey(timestamp: string): string {
  const date = dayjs(timestamp);
  return date.isValid() ? date.startOf("day").format("YYYY-MM-DD") : INVALID_DAY_KEY;
}

/**
 * Returns the pt-BR label for a day separator: "Hoje" for `now` itself,
 * "Ontem" for the previous day, "DD/MM/YYYY" for older days, and "Sem data"
 * when `dayKey` is not a valid date.
 *
 * @param dayKey - Local day key in `YYYY-MM-DD` format (see {@link timelineDayKey}).
 * @param now - Reference moment used to compute "Hoje"/"Ontem" (defaults to now).
 * @returns The separator label.
 */
export function timelineDayLabel(dayKey: string, now: Dayjs = dayjs()): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) {
    return "Sem data";
  }
  const day = dayjs(dayKey, "YYYY-MM-DD");
  if (!day.isValid()) {
    return "Sem data";
  }
  const today = now.startOf("day");
  if (day.isSame(today, "day")) {
    return "Hoje";
  }
  if (day.isSame(today.subtract(1, "day"), "day")) {
    return "Ontem";
  }
  return day.format("DD/MM/YYYY");
}

/**
 * Groups timeline entries by local day, preserving the order in which days
 * first appear (entries are expected to be sorted newest first, so the groups
 * come back newest day first). Entries with an unparsable anchor timestamp
 * land in a trailing "Sem data" group.
 *
 * @param entries - Timeline entries in display order.
 * @param getTimestamp - Resolves the anchor timestamp used for day grouping.
 * @param now - Reference moment for the day labels (defaults to now).
 * @returns Day groups in first-seen order, each with its entries in input order.
 */
export function groupTimelineByDay<TItem>(
  entries: TItem[],
  getTimestamp: (entry: TItem) => string,
  now: Dayjs = dayjs()
): TimelineDayGroup<TItem>[] {
  const groups: TimelineDayGroup<TItem>[] = [];
  const groupIndexByDay = new Map<string, number>();

  for (const entry of entries) {
    const dayKey = timelineDayKey(getTimestamp(entry));
    const groupIndex = groupIndexByDay.get(dayKey);
    if (groupIndex === undefined) {
      groupIndexByDay.set(dayKey, groups.length);
      groups.push({ dayKey, label: timelineDayLabel(dayKey, now), entries: [entry] });
    } else {
      groups[groupIndex].entries.push(entry);
    }
  }

  return groups;
}
